import { validateCpfAndCnpj } from 'v2/helpers/cpfCnpj';
import { CurrencyId } from 'v2/interfaces/financial';
import * as Yup from 'yup';

export enum accountBankType {
  CONTA_CORRENTE = 'CC',
  CONTA_POUPANCA = 'CP'
}

export enum PersonalDocumentType {
  RG = 'rg',
  CNH = 'cnh',
  PROFESSIONAL = 'professional_identity'
}

export type BankAccountDocumentType =
  PersonalDocumentType.RG | PersonalDocumentType.CNH | PersonalDocumentType.PROFESSIONAL;

export type NewBankAccount = {
  id: number;
  account_nature: 'F' | 'J';
  currency: CurrencyId;
  type: accountBankType.CONTA_CORRENTE | accountBankType.CONTA_POUPANCA;
  bank_id: string;
  operation_id: string;
  document_number: string;
  name: string;
  agency: string;
  agency_digit: string;
  account: string;
  account_digit: string;
  notes?: string;
  bank_receipt_image: File | null;
  document_image_type: BankAccountDocumentType;
  document_image_front?: File | null;
  document_image_back?: File | null;
  selfie_video: File | null;
  verify_code: string | null;
};

export const CAIXA_ECONOMICA_ID = '104';
export const BANCO_DO_BRASIL_ID = '001';

const accountNatureTypes = [
  {
    value: 'F',
    label: 'Conta pessoa Física'
  },
  {
    value: 'J',
    label: 'Conta pessoa Jurídica'
  }
];

const accountTypes = [
  {
    value: accountBankType.CONTA_CORRENTE,
    label: 'Conta corrente'
  },
  {
    value: accountBankType.CONTA_POUPANCA,
    label: 'Conta poupança'
  }
];

const currencyTypes = [
  {
    value: CurrencyId.BRL,
    label: 'R$'
  },
  {
    value: CurrencyId.USD,
    label: '$'
  }
];

const initialValues: NewBankAccount = {
  id: null,
  account_nature: 'J',
  currency: 1,
  type: accountBankType.CONTA_CORRENTE,
  bank_id: '',
  operation_id: '',
  document_number: '',
  name: '',
  agency: '',
  agency_digit: '',
  account: '',
  account_digit: '',
  notes: '',
  bank_receipt_image: null,
  document_image_type: PersonalDocumentType.RG,
  document_image_front: null,
  document_image_back: null,
  selfie_video: null,
  verify_code: ''
};

const isSelected = (value: string) => Boolean(value);
const testFileFormat = (formats: string[]) => (file: File) => (file ? formats.includes(file.type) : true);
const testFileSize = (maxSizeInMB: number) => (file: File) => {
  if (!file) return true;
  const fileSizeInMB = file.size / 1024 / 1024;
  return fileSizeInMB <= maxSizeInMB;
};

const validationsSchema = {
  national: Yup.object().shape({
    account_nature: Yup.string().required('A natureza da conta é obrigatória'),
    name: Yup.string().required('O nome é obrigatório'),
    currency: Yup.number().required('O tipo da moeda é obrigatório'),
    type: Yup.string().required('O tipo da conta é obrigatório'),
    bank_id: Yup.string()
      .required('Selecione um banco antes de continuar')
      .test('invalid', 'Selecione um banco antes de continuar', isSelected),
    operation_id: Yup.string().when('bank_id', {
      is: CAIXA_ECONOMICA_ID,
      then: Yup.string()
        .required('Selecione uma operação antes de continuar')
        .test('invalid', 'Selecione uma operação antes de continuar', isSelected)
    }),
    document_number: Yup.string()
      .required('O CPF/CNPJ é obrigatório')
      .test('invalid', 'CPF ou CNPJ inválido', validateCpfAndCnpj),
    agency: Yup.string().required('Informe o número da agência').matches(/^\d+$/, 'Campo aceita apenas números'),
    agency_digit: Yup.string().matches(/^[0-9]+$/, 'Campo aceita apenas números'),
    account: Yup.string().required('Informe o número da conta').matches(/^\d+$/, 'Campo aceita apenas números'),
    account_digit: Yup.string()
      .required('Informe o dígito da conta')
      .when('bank_id', {
        is: BANCO_DO_BRASIL_ID,
        then: Yup.string().matches(/[0-9xX]+$/, 'Campo aceita apenas números e "X"'),
        otherwise: Yup.string().matches(/^[0-9]+$/, 'Campo aceita apenas números')
      }),
    document_image_front: Yup.mixed()
      .when(['id', 'document_image_type'], {
        is: (id: number, documentImageType: string) =>
          typeof id !== 'number' && documentImageType === PersonalDocumentType.RG,
        then: Yup.mixed().required('Por favor, adicione a imagem da frente do RG')
      })
      .when(['id', 'document_image_type'], {
        is: (id: number, documentImageType: string) =>
          typeof id !== 'number' && documentImageType === PersonalDocumentType.CNH,
        then: Yup.mixed().required('Por favor, adicione a imagem da CNH')
      })
      .when(['id', 'document_image_type'], {
        is: (id: number, documentImageType: string) =>
          typeof id !== 'number' && documentImageType === PersonalDocumentType.PROFESSIONAL,
        then: Yup.mixed().required('Por favor, adicione a imagem da frente da Identidade Profissional')
      })
      .test(
        'Image format',
        'Informe um arquivo com um desses formatos: .jpg, .jpeg, .png ou .pdf',
        testFileFormat(['image/jpg', 'image/png', 'image/jpeg', 'application/pdf'])
      )
      .test('Image size', 'O arquivo deve ter no máximo 2MB', testFileSize(2)),
    document_image_back: Yup.mixed()
      .when(['id', 'document_image_type'], {
        is: (id: number, documentImageType: string) =>
          typeof id !== 'number' && documentImageType === PersonalDocumentType.RG,
        then: Yup.mixed().required('Por favor, adicione a imagem do verso do RG')
      })
      .when(['id', 'document_image_type'], {
        is: (id: number, documentImageType: string) =>
          typeof id !== 'number' && documentImageType === PersonalDocumentType.PROFESSIONAL,
        then: Yup.mixed().required('Por favor, adicione a imagem do verso da Identidade Profissional')
      })
      .test(
        'Image format',
        'Informe um arquivo com um desses formatos: .jpg, .jpeg, .png ou .pdf',
        testFileFormat(['image/jpg', 'image/png', 'image/jpeg', 'application/pdf'])
      )
      .test('Image size', 'O arquivo deve ter no máximo 2MB', testFileSize(2)),
    selfie_video: Yup.mixed()
      .when('id', {
        is: (id: number) => typeof id !== 'number',
        then: Yup.mixed().required('Por favor, adicione o vídeo selfie')
      })
      .test(
        'Video Format',
        'Informe um arquivo com um desses formatos: .avi, .mp4, .qtff, .wmv, .mkv, .webm',
        testFileFormat([
          'video/avi',
          'video/x-msvideo',
          'video/x-flv',
          'video/mp4',
          'video/quicktime',
          'video/x-ms-wmv',
          'video/x-matroska',
          'video/webm'
        ])
      )
      .test('Image size', 'O arquivo deve ter no máximo 35MB', testFileSize(35)),
    bank_receipt_image: Yup.mixed()
      .when('id', {
        is: (id: number) => typeof id !== 'number',
        then: Yup.mixed().required('Informe o comprovante bancário')
      })
      .test(
        'Image format',
        'Informe um arquivo com um desses formatos: .jpg, .jpeg, .png ou .pdf',
        testFileFormat(['image/jpg', 'image/png', 'image/jpeg', 'application/pdf'])
      )
      .test('Image size', 'O arquivo deve ter no máximo 2MB', testFileSize(2)),
    verify_code: Yup.string().matches(/^\d+$/, 'Campo aceita apenas números').length(6)
  }),
  crossborder: Yup.object().shape({
    account_nature: Yup.string().required('A natureza da conta é obrigatória'),
    name: Yup.string().required('O nome é obrigatório'),
    currency: Yup.number().required('O tipo da moeda é obrigatório'),
    type: Yup.string().required('O tipo da conta é obrigatório'),
    bank_id: Yup.string()
      .required('Selecione um banco antes de continuar')
      .test('invalid', 'Selecione um banco antes de continuar', isSelected),
    document_number: Yup.string().required('Número de documento é obrigatório'),
    agency: Yup.string().required('Informe o número da agência').matches(/^\d+$/, 'Campo aceita apenas números'),
    account: Yup.string().required('Informe o número da conta').matches(/^\d+$/, 'Campo aceita apenas números'),
    notes: Yup.string(),
    document_image_front: Yup.mixed()
      .when(['id', 'document_image_type'], {
        is: (id: number, documentImageType: string) =>
          typeof id !== 'number' && documentImageType === PersonalDocumentType.RG,
        then: Yup.mixed().required('Por favor, adicione a imagem da frente do RG')
      })
      .when(['id', 'document_image_type'], {
        is: (id: number, documentImageType: string) =>
          typeof id !== 'number' && documentImageType === PersonalDocumentType.CNH,
        then: Yup.mixed().required('Por favor, adicione a imagem da CNH')
      })
      .when(['id', 'document_image_type'], {
        is: (id: number, documentImageType: string) =>
          typeof id !== 'number' && documentImageType === PersonalDocumentType.PROFESSIONAL,
        then: Yup.mixed().required('Por favor, adicione a imagem da frente da Identidade Profissional')
      })
      .test(
        'Image format',
        'Informe um arquivo com um desses formatos: .jpg, .jpeg, .png ou .pdf',
        testFileFormat(['image/jpg', 'image/png', 'image/jpeg', 'application/pdf'])
      )
      .test('Image size', 'O arquivo deve ter no máximo 2MB', testFileSize(2)),
    document_image_back: Yup.mixed()
      .when(['id', 'document_image_type'], {
        is: (id: number, documentImageType: string) =>
          typeof id !== 'number' && documentImageType === PersonalDocumentType.RG,
        then: Yup.mixed().required('Por favor, adicione a imagem do verso do RG')
      })
      .when(['id', 'document_image_type'], {
        is: (id: number, documentImageType: string) =>
          typeof id !== 'number' && documentImageType === PersonalDocumentType.PROFESSIONAL,
        then: Yup.mixed().required('Por favor, adicione a imagem do verso da Identidade Profissional')
      })
      .test(
        'Image format',
        'Informe um arquivo com um desses formatos: .jpg, .jpeg, .png ou .pdf',
        testFileFormat(['image/jpg', 'image/png', 'image/jpeg', 'application/pdf'])
      )
      .test('Image size', 'O arquivo deve ter no máximo 2MB', testFileSize(2)),
    selfie_video: Yup.mixed()
      .when('id', {
        is: (id: number) => typeof id !== 'number',
        then: Yup.mixed().required('Por favor, adicione o vídeo selfie')
      })
      .test(
        'Video Format',
        'Informe um arquivo com um desses formatos: .avi, .mp4, .qtff, .wmv, .mkv, .webm',
        testFileFormat([
          'video/avi',
          'video/x-msvideo',
          'video/x-flv',
          'video/mp4',
          'video/quicktime',
          'video/x-ms-wmv',
          'video/x-matroska',
          'video/webm'
        ])
      )
      .test('Image size', 'O arquivo deve ter no máximo 35MB', testFileSize(35)),
    bank_receipt_image: Yup.mixed()
      .when('id', {
        is: (id: number) => typeof id !== 'number',
        then: Yup.mixed().required('Informe o comprovante bancário')
      })
      .test(
        'Image format',
        'Informe um arquivo com um desses formatos: .jpg, .jpeg, .png ou .pdf',
        testFileFormat(['image/jpg', 'image/png', 'image/jpeg', 'application/pdf'])
      )
      .test('Image size', 'O arquivo deve ter no máximo 2MB', testFileSize(2)),
    verify_code: Yup.string().matches(/^\d+$/, 'Campo aceita apenas números').length(6)
  })
};

const fieldsByStep = {
  national: [
    [
      'account_nature',
      'name',
      'currency',
      'type',
      'bank_id',
      'operation_id',
      'document_number',
      'agency',
      'agency_digit',
      'account',
      'account_digit'
    ],
    ['document_image_front', 'document_image_back', 'selfie_video'],
    ['bank_receipt_image'],
    [],
    ['verify_code']
  ],
  crossborder: [
    ['account_nature', 'name', 'currency', 'type', 'bank_id', 'document_number', 'agency', 'account', 'notes'],
    ['document_image_front', 'document_image_back', 'selfie_video'],
    ['bank_receipt_image'],
    [],
    ['verify_code']
  ]
};

const getSchema = (isCrossborder: boolean) =>
  isCrossborder ? validationsSchema.crossborder : validationsSchema.national;

export { initialValues, getSchema, accountTypes, currencyTypes, accountNatureTypes, fieldsByStep };
